import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { down, up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled, { css } from 'styled-components';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { BottomDrawer } from 'src/components/pages/conversations/components/drawer/bottom-drawer';
import { ellipsisTextStyle } from 'src/components/pages/dashboard/components/shared';
import { useVisible } from 'src/hooks/use-visible';
import { LggButton } from '../button/lgg-button';
import { UnDecoratedTextInput } from './text-input';

const InputContainer = styled(FlexRow)`
  align-items: flex-start;
  justify-content: space-between;

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  ${down('md')} {
    input.input-error {
      border: none;
    }
  }
`;

const inlineFormPlaceholderStyles = css`
  color: ${({ theme }) => theme.colors.geyser};
`;

export const inlineFormInputStyles = css`
  border: 1px solid transparent;
  border-radius: 4px;
  caret-color: ${({ theme }) => theme.colors.lightBlue};
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  height: 32px;
  line-height: 16px;
  outline: none;
  padding: 8px 5px;
  transition: border 150ms, box-shadow 150ms;
  width: 100%;

  &:disabled,
  &:hover {
    background: ${({ theme }) => theme.colors.alabaster};
    border-radius: 4px;
  }

  &:focus {
    ${({ theme }) => theme.inputsTheme.focusBorder};
  }

  ${up('md')} {
    ${InputContainer}.input-error & {
      ${({ theme }) => theme.inputsTheme.errorBorder};
    }
  }

  &::-webkit-input-placeholder {
    ${inlineFormPlaceholderStyles};
  }
  &::-moz-placeholder {
    ${inlineFormPlaceholderStyles};
  }
  &::-ms-input-placeholder {
    ${inlineFormPlaceholderStyles};
  }
  &::placeholder {
    ${inlineFormPlaceholderStyles};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const StyledInlineTextInput = styled.input`
  ${inlineFormInputStyles};
  ${ellipsisTextStyle};

  ${down('md')} {
    border: 1px solid ${({ theme }) => theme.colors.koala};
  }
`;

const MobileFooterContainer = styled(FlexRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  gap: 10px;
  padding: 15px 20px;
`;

export const InputFooterActions = ({
  onSave,
  onCancel,
}: {
  onSave: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation(['common']);

  return (
    <MobileFooterContainer>
      <LggButton variant="tertiary" onClick={onCancel}>
        {t('common:cancel')}
      </LggButton>
      <LggButton variant="primary" onClick={onSave}>
        {t('common:save')}
      </LggButton>
    </MobileFooterContainer>
  );
};

export const PivotTextInput = styled.input`
  ${inlineFormInputStyles};
  ${ellipsisTextStyle};

  text-align: right;
  width: 100% !important;
`;

export const InlineUnDecoratedTextInput = styled(UnDecoratedTextInput)`
  input {
    ${inlineFormInputStyles};
    border: 2px solid transparent !important;
    height: 38px;

    &:hover:not(:focus):not(:active) {
      background: ${({ theme }) => theme.colors.porcelain};
      border: none;
      border-color: transparent;
      box-shadow: none;
      outline: none;
    }

    &:active,
    &:focus {
      ${({ theme }) => theme.inputsTheme.focusBorder};
      border: 2px solid ${({ theme }) => theme.colors.gogo} !important;
      box-shadow: 0 3px 6px 0 rgba(165, 209, 223, 0.32) !important;
    }

    ${up('md')} {
      border: inherit;
      height: 32px;
      padding-left: 37px;
    }

    ${down('md')} {
      border: 1px solid ${({ theme }) => theme.colors.koala} !important;
    }
  }

  .lgg-icon {
    display: none;

    ${up('md')} {
      display: block;
    }

    svg {
      width: 16px;

      path {
        fill: ${({ theme }) => theme.colors.flint};
      }
    }
  }
`;

export type TextInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  leadingIcon?: string;
  error?: string | boolean;
  reserveErrorArea?: boolean;
  containerStyles?: React.CSSProperties;
  showErrorMessage?: boolean;
};

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string | boolean;
  showErrorMessage?: boolean;
};

type InlineInputProps = {
  label: string;
  icon?: string;
  error?: string | boolean;
  showErrorMessage?: boolean;
  children: React.ReactNode;
};

type InlineTextInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> &
  Omit<InlineInputProps, 'children'> & {
    value?: string | number | null;
  };

const InputIcon = styled(Icon)`
  height: 16px;
  margin-right: 10px;
  width: 16px;

  svg {
    height: 16px;
    width: 16px;
    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const InlineTextInputLabel = styled(FlexRow)`
  align-items: center;
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 15px;
`;

const InlineTextInputError = styled.span`
  ${({ theme }) => theme.inputsTheme.errorStyle};
  bottom: -10px;
  color: ${({ theme }) => theme.colors.secondaryCoral};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 9px;
  line-height: 10px;
  position: absolute;
  right: 0;

  ${InputContainer}.input-error & {
    opacity: 1;
  }

  ${up('md')} {
    bottom: -10px;
    font-size: 8px;
    left: 0;
  }
`;

const StyledInputContainer = styled(FlexColumn)`
  position: relative;
  width: max-content;

  align-items: flex-end;
  flex: 1;

  & > * {
    width: max-content;
  }

  max-width: 69%;

  ${up('md')} {
    width: 100%;

    & > * {
      width: 100%;
    }
  }

  & > .lgg-date-time-picker-input {
    & > .ant-picker {
      border: none;
      height: 32px;

      ${inlineFormInputStyles};

      ${down('md')} {
        input {
          field-sizing: content;
        }

        &,
        &.ant-picker-focused {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
      }

      .ant-picker-suffix,
      .ant-picker-clear {
        display: none;
      }

      &:focus,
      &:hover {
        .ant-picker-suffix,
        .ant-picker-clear {
          display: flex;
        }

        .ant-picker-clear {
          background-color: ${({ theme }) => theme.colors.porcelain};
        }
      }
    }
  }

  .lgg-date-picker-input {
    border: none;
    transition: border 0s, box-shadow 0s, outline 0s;

    &.ant-picker {
      height: 32px;
    }

    &:hover {
      border: none;
    }

    &.ant-picker-focused {
      &,
      &:hover {
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
      }
    }

    input {
      text-align: right;

      &:active,
      &:focus {
        border: none;
      }

      &::placeholder {
        text-align: right;
      }
    }
  }
`;

const InlineInputLabel = styled(FlexRow)`
  padding: 8px 0px;
`;

export const InlineInputWrapper = ({
  label,
  error,
  showErrorMessage = true,
  icon,
  children,
}: Omit<InlineInputProps, 'label'> & {
  label: React.ReactNode;
}) => {
  const hasError = Boolean(error);

  return (
    <InputContainer
      className={c({
        'input-error': hasError,
      })}
    >
      <InlineInputLabel className="lgg-text-input-label">
        {icon && <InputIcon type={icon} />}
        <InlineTextInputLabel>{label}</InlineTextInputLabel>
      </InlineInputLabel>
      <StyledInputContainer>
        {children}
        {showErrorMessage && error && (
          <InlineTextInputError data-lgg-id="lgg-text-input-error">
            {error}
          </InlineTextInputError>
        )}
      </StyledInputContainer>
    </InputContainer>
  );
};

const MobileInputWrapper = styled.span`
  display: flex;
  padding: 5px 5px 20px 5px;
`;

export const InlineTextInput = React.forwardRef<HTMLInputElement, InlineTextInputProps>(
  ({ label, error, value, showErrorMessage = true, icon, onChange, ...rest }, ref) => {
    const breakpointUpMd = useBreakpoint(up('md'));
    const visibilityHandler = useVisible();
    const fieldValue = value ?? '';
    const [pivotValue, setPivotValue] = useState(fieldValue);
    const drawerInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (visibilityHandler.visible) {
        setPivotValue(value ?? '');
        drawerInputRef.current?.focus();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibilityHandler.visible]);

    return (
      <InlineInputWrapper
        label={label}
        error={error}
        showErrorMessage={showErrorMessage}
        icon={icon}
      >
        {breakpointUpMd ? (
          <StyledInlineTextInput
            ref={ref}
            className={c('lgg-text-input', { 'input-error': error })}
            value={value || ''}
            onChange={onChange}
            {...rest}
          />
        ) : (
          <>
            <PivotTextInput
              ref={ref}
              className={c('lgg-text-input', { 'input-error': error })}
              value={value || ''}
              {...rest}
              onClick={visibilityHandler.show}
              readOnly
            />
            <BottomDrawer
              title={label}
              onClose={visibilityHandler.close}
              visible={visibilityHandler.visible}
              push={false}
              footer={
                <InputFooterActions
                  onSave={() => {
                    onChange?.({
                      target: { value: pivotValue },
                    } as React.ChangeEvent<HTMLInputElement>);
                    visibilityHandler.close();
                  }}
                  onCancel={visibilityHandler.close}
                />
              }
            >
              <MobileInputWrapper>
                <StyledInlineTextInput
                  ref={drawerInputRef}
                  className="lgg-text-input"
                  onChange={(e) => setPivotValue(e.target.value)}
                  value={pivotValue || ''}
                  {...rest}
                />
              </MobileInputWrapper>
            </BottomDrawer>
          </>
        )}
      </InlineInputWrapper>
    );
  },
);

const BigTextInputContainer = styled(FlexColumn)`
  position: relative;
  width: 100%;
`;

const BigTextInputLabel = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  left: 11px;
  letter-spacing: -0.5px;
  line-height: 15px;
  position: absolute;
  top: 5px;
`;

const bigTextInputStyles = css`
  ${inlineFormInputStyles};
  border-radius: 4px;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  height: 46px;
  letter-spacing: -0.36px;
  line-height: 23px;
  padding: 18px 10px 5px 10px;
  ${ellipsisTextStyle};

  &.input-error {
    border: 1px solid ${({ theme }) => theme.colors.secondaryCoral60};
  }
`;

const StyledBigTextInput = styled.input`
  ${bigTextInputStyles};
`;

const StyledBigTextArea = styled.textarea`
  ${bigTextInputStyles};

  field-sizing: content;
  height: max-content;
  resize: none;
  white-space: break-spaces;
`;

const BigTextInputError = styled.span`
  color: ${({ theme }) => theme.colors.secondaryCoral};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 9px;
  font-weight: 500;
  line-height: 10px;
  position: absolute;
  right: 10px;
  top: 7px;
`;

export const BigTextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, error, value, ...rest }, ref) => {
    return (
      <BigTextInputContainer>
        <BigTextInputLabel>{label}</BigTextInputLabel>
        <StyledBigTextInput
          ref={ref}
          className={c({ 'input-error': error })}
          value={value || ''}
          {...rest}
        />
        {error ? <BigTextInputError>{error}</BigTextInputError> : null}
      </BigTextInputContainer>
    );
  },
);

export const BigTextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, error, value, ...rest }, ref) => {
    return (
      <BigTextInputContainer>
        <BigTextInputLabel>{label}</BigTextInputLabel>
        <StyledBigTextArea
          ref={ref}
          className={c({ 'input-error': error })}
          value={value || ''}
          {...rest}
        />
        {error ? <BigTextInputError>{error}</BigTextInputError> : null}
      </BigTextInputContainer>
    );
  },
);

const twoLinesTextAreaClampStyles = css`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  line-clamp: 2;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: pre-wrap;
`;

const InlineFormTextAreaMobileDisplay = styled.textarea`
  ${inlineFormInputStyles};
  ${twoLinesTextAreaClampStyles};

  field-sizing: content;
  height: max-content;
  line-height: 19px;
  max-height: 52px;
  min-height: 32px;
  overflow: hidden;
  padding: 5px 1px 4px 5px;
  resize: none;
  text-align: right;
  white-space: break-spaces;
`;

const InlineFormTextArea = styled.textarea`
  ${inlineFormInputStyles};

  field-sizing: content;
  height: max-content;
  line-height: 18px;
  overflow: hidden;
  padding-bottom: 6px;
  padding-top: 6px;
  resize: none;
  text-align: left;

  ${down('md')} {
    height: 180px;
    padding: 10px;

    &:not(:focus) {
      border: 1px solid ${({ theme }) => theme.colors.koala};
    }
  }

  ${up('md')} {
    text-align: left;
    max-height: 47px;
    min-height: 32px;

    &:not(:focus-within) {
      ${twoLinesTextAreaClampStyles};
    }

    &:empty {
      max-height: 32px;
    }
  }
`;

type InlineTextAreaProps = Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, 'value'> &
  Omit<InlineInputProps, 'children'> & {
    value?: string | number | null;
  };

export const InlineTextArea = React.forwardRef<HTMLTextAreaElement, InlineTextAreaProps>(
  ({ label, error, value, showErrorMessage = true, icon, onChange, ...rest }, ref) => {
    const breakpointUpMd = useBreakpoint(up('md'));
    const visibilityHandler = useVisible();
    const [pivotValue, setPivotValue] = useState(value || '');

    useEffect(() => {
      setPivotValue(value || '');
    }, [value]);

    return (
      <InlineInputWrapper
        label={label}
        error={error}
        showErrorMessage={showErrorMessage}
        icon={icon}
      >
        {breakpointUpMd ? (
          <InlineFormTextArea
            ref={ref}
            className={c('lgg-text-input', { 'input-error': error })}
            value={value || ''}
            onChange={onChange}
            {...rest}
          />
        ) : (
          <>
            <InlineFormTextAreaMobileDisplay
              className={c('lgg-text-input', { 'input-error': error })}
              value={value || ''}
              {...rest}
              onClick={visibilityHandler.show}
              readOnly
            />
            <BottomDrawer
              title={label}
              onClose={visibilityHandler.close}
              visible={visibilityHandler.visible}
              push={false}
              footer={
                <InputFooterActions
                  onSave={() => {
                    onChange?.({
                      target: { value: pivotValue },
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                    visibilityHandler.close();
                  }}
                  onCancel={visibilityHandler.close}
                />
              }
            >
              <MobileInputWrapper>
                <InlineFormTextArea
                  ref={ref}
                  className="lgg-text-input"
                  onChange={(e) => setPivotValue(e.target.value)}
                  {...rest}
                  value={pivotValue}
                />
              </MobileInputWrapper>
            </BottomDrawer>
          </>
        )}
      </InlineInputWrapper>
    );
  },
);
